/**
 * The Wallet shell's navigation model — DERIVED FROM `PAGE_REGISTRY`, never
 * hand-listed, for exactly the reason `src/routes.tsx` generates the route
 * table from the same place: a destination the registry does not know has no
 * permission check.
 *
 * THE RULE THIS FILE ENFORCES
 * --------------------------
 * Every href drawn by this shell must fall into one of three buckets, and
 * `classifyHref()` below decides which:
 *
 *   1. REGISTRY-BACKED — `findPageByPath(href)` resolves it, so `guardRoute`
 *      gates it by that page's `PagePermission` row, and the entry is drawn
 *      only when the key is in `useVisiblePages()`.
 *   2. ADMIN-PREFIXED — `isAdminOnlyPath(href)` is true, so the guard decides
 *      by ROLE before the registry is ever consulted. Drawn only for admins.
 *   3. SHELL — one of the non-registry routes the parity test already accounts
 *      for (`/notifications`, `/preferences`, `/reports`, plus the public ones
 *      this shell never links). Authenticated-only BY DESIGN.
 *
 * Anything else is `"unrouted"`, and `unlinkableHrefs()` reports it. The shell
 * calls that in dev and throws, so a link to a page nobody registered fails
 * loudly here rather than quietly becoming an ungated route later.
 *
 * NO ROUTE IS ADDED BY THIS FILE. It only chooses which of the routes
 * `routes.tsx` already generates get a tile in the navigation.
 */
import {
  Bell,
  CalendarClock,
  FileBarChart,
  FolderTree,
  LayoutGrid,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Users,
  type LucideIcon,
} from "lucide-react";
import { PAGE_REGISTRY, findPageByPath, findPageByKey, type PageKey } from "@/lib/rbac/pages";
import { MODULES } from "./modules";
import { isAdminOnlyPath } from "@/lib/rbac/route-guard";

export interface NavEntry {
  href: string;
  label: string;
  icon: LucideIcon;
  /** Present when a `PAGE_REGISTRY` row gates this destination. */
  pageKey?: PageKey;
  /** Non-registry routes gated by ROLE (`/admin*`, `/settings*`). */
  requiresAdmin?: boolean;
  /** One line of "what is in here", shown under the label in the More sheet. */
  hint?: string;
}

export interface NavSection {
  title: string;
  entries: NavEntry[];
}

/**
 * The non-registry paths a signed-in user may open. Mirrors the `SHELL` list
 * in `frontend/web/tests/parity.test.ts` — if that list changes, this one has to, and
 * `unlinkableHrefs()` is what makes the mismatch visible.
 */
export const SHELL_PATHS = [
  "/login",
  "/access-request-pending",
  "/no-access",
  "/preferences",
  "/notifications",
  "/reports",
  // Routed outside the registry (`routes.tsx`); linked from the desktop
  // frame's sidebars.
  "/groups",
  "/security",
  "/settings/recycle-bin",
] as const;

export type HrefKind = "registry" | "admin" | "shell" | "unrouted";

export function classifyHref(href: string): HrefKind {
  const path = href.split("?")[0];
  if (isAdminOnlyPath(path)) return "admin";
  if (findPageByPath(path)) return "registry";
  if ((SHELL_PATHS as readonly string[]).includes(path)) return "shell";
  return "unrouted";
}

/* ──────────────────────────── the bottom bar ─────────────────────────────
 * Wallet's five slots: Home · Records · [+] · Statistics · More.
 *
 * The three navigable ones are registry pages that ALREADY EXIST —
 * `dashboard`, `records`, `analytics`. The centre `[+]` is not a destination
 * at all: it opens the entry sheet as an OVERLAY inside whatever route you are
 * on, which is why it needs no route and cannot bypass a gate. Its own
 * visibility is still tied to a permission (see `ENTRY_PAGE_KEYS`) so a user
 * who may not write records is not offered a button that would 403.
 */
export const HOME_HREF = "/dashboard";
export const RECORDS_HREF = "/records";
export const STATS_HREF = "/analytics";

/**
 * The add button is drawn when the user may see ANY of these — the pages whose
 * data an entry creates. The write itself is authorised server-side on every
 * request; this only decides whether a dead button is drawn.
 */
export const ENTRY_PAGE_KEYS: PageKey[] = ["records", "income", "expenses"];

export interface PrimaryTab {
  key: "home" | "records" | "stats";
  href: string;
  label: string;
  pageKey: PageKey;
}

export const PRIMARY_TABS: PrimaryTab[] = [
  { key: "home", href: HOME_HREF, label: "Home", pageKey: "dashboard" },
  { key: "records", href: RECORDS_HREF, label: "Records", pageKey: "records" },
  { key: "stats", href: STATS_HREF, label: "Statistics", pageKey: "analytics" },
];

/** Keys the bottom bar already covers — excluded from the More surface. */
const PRIMARY_KEYS = new Set<PageKey>(PRIMARY_TABS.map((t) => t.pageKey));

/* ───────────────────────────── the desktop rail ──────────────────────────
 * At `lg` and up the bottom bar gives way to a left rail (the approved "AIM
 * Wallet" replica): the daily views flat at the top, then the rest of AIM
 * grouped exactly as the More sheet groups it. Same registry, same gates —
 * a rail entry is drawn only when its page key is in the visible set.
 */
export interface RailEntry {
  href: string;
  label: string;
  pageKey: PageKey;
}

export const RAIL_PRIMARY: RailEntry[] = [
  { href: HOME_HREF, label: "Home", pageKey: "dashboard" },
  { href: "/accounts", label: "Accounts", pageKey: "accounts" },
  { href: RECORDS_HREF, label: "Records", pageKey: "records" },
  { href: STATS_HREF, label: "Statistics", pageKey: "analytics" },
  { href: "/budget", label: "Budgets", pageKey: "budget" },
  { href: "/loans", label: "Debts", pageKey: "loans" },
];

const RAIL_KEYS = new Set<PageKey>(RAIL_PRIMARY.map((e) => e.pageKey));

/**
 * Registry pages the rail does NOT list, because another rail entry already
 * reaches them (owner's decision 2026-09-25): Income / Expenses are filtered
 * views of Records, the Home Loan Module opens from its Loan, and the Yearly
 * Summary is a tab of Statistics. The pages and their routes stay; only the
 * duplicate rail links go. The phone's More sheet is unchanged.
 */
const RAIL_HIDDEN = new Set<PageKey>(["income", "expenses", "home-loan", "yearly-summary"]);

/**
 * The rail's lower groups: the More surface minus what the rail already lists
 * flat. Built from `moreSections` so the two navigations cannot drift.
 */
export function railSections(visible: ReadonlySet<PageKey>, isAdmin: boolean): NavSection[] {
  const tabs = RAIL_TABS.filter((t) => visible.has(t.pageKey));
  return moreSections(visible, isAdmin)
    .map((section) => ({
      ...section,
      entries: [
        ...section.entries.filter(
          (e) => !e.pageKey || (!RAIL_KEYS.has(e.pageKey) && !RAIL_HIDDEN.has(e.pageKey)),
        ),
        // Tab entries join the group their host page belongs to.
        ...(section.title === SECTION_TITLES.money ? tabs : []),
      ],
    }))
    .filter((section) => section.entries.length > 0);
}

/**
 * Rail entries that are a TAB of a registry page rather than a page of their
 * own. The approved replica's rail lists Planned beside Budgets; giving it a
 * `PAGE_REGISTRY` key of its own would need a `PagePermission` row backfilled
 * for all eight family members first (a missing row is a DENY), so it is a tab
 * of `/budget` and inherits that page's permission. `classifyHref` strips the
 * query, so this still resolves to a registry-backed, gated destination.
 */
export const RAIL_TABS: (NavEntry & { pageKey: PageKey })[] = [
  {
    href: "/budget?tab=planned",
    label: "Planned",
    icon: CalendarClock,
    pageKey: "budget",
    hint: "Standing commitments and recurring payments",
  },
];

/**
 * Which rail entry owns the current location.
 *
 * An entry carrying a query (a tab entry) matches only on an EXACT
 * `pathname?search`; a plain entry matches by longest path prefix, but loses
 * to a tab entry that matched exactly — otherwise `/budget?tab=planned` would
 * light up Budgets and Planned at once.
 */
export function activeRailHref(current: string, hrefs: string[]): string | null {
  const [pathname] = current.split("?");
  for (const href of hrefs) {
    if (href.includes("?") && href === current) return href;
  }
  let best: string | null = null;
  for (const href of hrefs) {
    if (href.includes("?")) continue;
    if (pathname === href || pathname.startsWith(href + "/")) {
      if (best === null || href.length > best.length) best = href;
    }
  }
  return best ?? (pathname === "/" ? HOME_HREF : null);
}

/* ───────────────────────────── the More surface ──────────────────────────
 * Everything Wallet does not have but AIM does.
 *
 * The GROUPING is editorial and lives here; the MEMBERSHIP does not — it is
 * `PAGE_REGISTRY` minus the three keys the bottom bar already covers. A
 * registry page missing from `SECTION_OF` still appears, under "More", so
 * adding a page to the registry can never leave it unreachable from this
 * shell. That is the same append-safety rule `routes.tsx` follows.
 */
type SectionId = "money" | "property" | "wealth" | "library" | "tools" | "other";

const SECTION_TITLES: Record<SectionId, string> = {
  money: "Money",
  property: "Property & tax",
  wealth: "Wealth",
  library: "Library",
  tools: "Reports & alerts",
  other: "More",
};

const SECTION_ORDER: SectionId[] = ["money", "property", "wealth", "library", "tools", "other"];

const SECTION_OF: Partial<Record<PageKey, SectionId>> = {
  accounts: "money",
  income: "money",
  expenses: "money",
  budget: "money",
  rent: "property",
  itr: "property",
  loans: "property",
  "home-loan": "property",
  "yearly-summary": "property",
  invest: "wealth",
  vault: "library",
  templates: "library",
};

/** Hand-written where the label alone does not say what is inside. */
const HINTS: Partial<Record<PageKey, string>> = {
  budget: "Budgets and savings goals",
  accounts: "Balances across every bank account",
  rent: "Properties, tenants, agreements, dues",
  itr: "Tax records and calculators",
  loans: "EMIs, amortisation, prepayments",
  "home-loan": "Home, car, personal, education and card EMIs",
  "yearly-summary": "The financial year at a glance",
  invest: "Portfolio and holdings",
  vault: "Family documents and warranties",
  templates: "Reusable entry templates",
};

/** Non-registry destinations, gated as described in the module note. */
const SHELL_ENTRIES: NavEntry[] = [
  { href: "/reports", label: "Reports", icon: FileBarChart, hint: "Build and export a report" },
  {
    href: "/notifications",
    label: "Notifications",
    icon: Bell,
    hint: "Everything the app has told you",
  },
];

/** The Admin Console — same role gate (`isAdminOnlyPath`), same links as the
 *  TopBar avatar menu, drawn here instead. Wave 6 (B5): one consolidated
 *  `/admin` hub (Overview / People / Data / System) replaces the old five
 *  sibling links — see `TopBar.tsx`'s `adminItems` for the full rationale. */
export const ADMIN_ENTRIES: NavEntry[] = [
  { href: "/admin", label: "Overview", icon: Settings, requiresAdmin: true },
  { href: "/admin/people", label: "People", icon: Users, requiresAdmin: true },
  { href: "/admin/modules", label: "Modules", icon: FolderTree, requiresAdmin: true },
  { href: "/admin/system", label: "System", icon: ShieldCheck, requiresAdmin: true },
];

export const PREFERENCES_ENTRY: NavEntry = {
  href: "/preferences",
  label: "Preferences",
  icon: SlidersHorizontal,
};

function registryEntry(key: PageKey): NavEntry | null {
  const page = findPageByKey(key);
  if (!page) return null;
  return { href: page.href, label: page.label, icon: page.icon, pageKey: key, hint: HINTS[key] };
}

/**
 * The More surface for one user.
 *
 * `visible` is `useVisiblePages()` — the set already computed from the user's
 * `PagePermission` rows by `AuthProvider`. A page the user may not view is not
 * drawn; the route would still deny them, this just avoids the dead tile.
 */
export function moreSections(visible: ReadonlySet<PageKey>, isAdmin: boolean): NavSection[] {
  const buckets = new Map<SectionId, NavEntry[]>();
  for (const id of SECTION_ORDER) buckets.set(id, []);

  for (const page of PAGE_REGISTRY) {
    if (PRIMARY_KEYS.has(page.key)) continue;
    if (!visible.has(page.key)) continue;
    // `adminOnly` pages (home-loan) are backend-enforced for role ADMIN; the
    // visible set already excludes them for a USER, this is belt and braces.
    if (page.adminOnly && !isAdmin) continue;
    const entry = registryEntry(page.key);
    if (entry) buckets.get(SECTION_OF[page.key] ?? "other")?.push(entry);
  }

  buckets.get("tools")?.push(...SHELL_ENTRIES);

  const sections: NavSection[] = SECTION_ORDER.map((id) => ({
    title: SECTION_TITLES[id],
    entries: buckets.get(id) ?? [],
  })).filter((section) => section.entries.length > 0);

  if (isAdmin) sections.push({ title: "Admin console", entries: ADMIN_ENTRIES });
  return sections;
}

/**
 * Dev-time self-check: every href this shell can draw must be routed AND
 * gated. Returns the offenders, which should always be empty.
 */
export function unlinkableHrefs(): string[] {
  const all = [
    ...PRIMARY_TABS.map((t) => t.href),
    ...RAIL_PRIMARY.map((e) => e.href),
    ...PAGE_REGISTRY.map((p) => p.href),
    ...SHELL_ENTRIES.map((e) => e.href),
    ...ADMIN_ENTRIES.map((e) => e.href),
    PREFERENCES_ENTRY.href,
    // The desktop frame's module bar + sidebars (`modules.ts`).
    ...MODULES.flatMap((m) => [m.href, ...m.groups.flatMap((g) => g.items.map((i) => i.href))]),
  ];
  return all.filter((href) => classifyHref(href) === "unrouted");
}

/** Which primary tab (if any) owns a pathname — drives the active state. */
export function activeTab(pathname: string): PrimaryTab["key"] | "more" | null {
  for (const tab of PRIMARY_TABS) {
    if (pathname === tab.href || pathname.startsWith(tab.href + "/")) return tab.key;
  }
  return pathname === "/" ? "home" : "more";
}

export const MORE_ICON: LucideIcon = LayoutGrid;
